import {FilterTag, TripOption, SuggestionGroup, SuggestionPois} from '@/views/amex/app-types';
import {
  AvailableTransportationModes,
  Price,
  UserActivityCategoryEnum,
  Activity,
  CandidatePoiItem,
  Timeslot,
} from '@mobi-components/global-solution';
import {Dictionary} from 'vue-router/types/router';
import {IGetAlternativesResponse} from '../api/load-alternatives';
import {ExtendedSolutionResponse, SolutionResponse} from '../solution/solution-support.types';
import {TripIntent, RecommendationContext} from '@mobi-components/bluesky-utils';
import {ISolutionVersion} from '../api/submit-amex-problem';
import {Trip, TripWithPois} from '../api/download-destination-trips';
import {TravelersFilterValue} from '@common/travelers-filter/travelers.types';
import {BudgetFilterValue} from '@mobi-components/amex-bluesky-components';
export * from '@mobi-components/bluesky-utils';

export enum DomainSection {
  PLACES = 'Places',
  TYPES = 'Types',
  NONE = '',
}

export interface IDomainCategoryDescription {
  category: UserActivityCategoryEnum;
  source: DomainSection;
  name: string;
  parent: string;
  id: string;
}
export interface IDomainTag {
  category: UserActivityCategoryEnum;
  name: string;
  source: DomainSection;
  parent: string;
}

export interface ProblemDestination {
  name: string;
  id: string;
  lat: number;
  lng: number;
  arrival_timestamp?: number;
  departure_timestamp?: number;
  relaxable?: boolean;
  time_zone: string;
  allowed_modes: AvailableTransportationModes[];
  type: string;
}

export interface SolutionUpdateDetails {
  activity_id: string;
  updated_inventory: boolean;
  updated_price: Price;
}

export interface IntersectionalFilter {
  title: string;
  value: string | null;
}

export interface SavedTrip {
  autoSaved: boolean;
  name: string;
  trip: TripOption;
  filters: FilterTag[];
  places: RecommendationContext[];
  origin: ProblemDestination | null;
  destination: ProblemDestination | null;
  deaprtureDate: number;
  arrivalDate: number;
  recommendations: TripOption[];
  solutionResponse: SolutionResponse | null;
  originalAccommodation: Activity | null;
  solutionUpdates: SolutionUpdateDetails[];
  alternativeActivities: IGetAlternativesResponse | null;
  tripId: string;
  id: string;
}
// export interface TimeFrameMode {
//   dest: boolean;
//   group: boolean;
//   solution: boolean;
// }

// export const defaultTimeFrameMode: TimeFrameMode = {
//   dest: true,
//   group: true,
//   solution: true,
// };
export interface RecentSearch {
  filters: FilterTag[];
  img: string;
  recommendations: TripOption[];
  id: string;
}

export interface NearbyTripOptions {
  contexts: TripOption[];
  isDestAvailable: boolean;
}
export interface FallbackImgs {
  stayImg: string;
  flightImg: string[];
  expImg: string[];
}
export interface IStore {
  origin: ProblemDestination | null;
  destination: ProblemDestination | null;
  problemId: string;
  userId: string;
  recommendationDetails: Dictionary<SuggestionPois[]>;
  selectedTrip: string;
  favorites: Record<string, MinRecommendation[]>;
  solutionResponse: ExtendedSolutionResponse | null;
  isSolutionLoading: boolean;
  alternatives: Record<string, AlternativesCollection>;
  myTrips: SavedTrip[];
  manualActivities: RecommendationContext[];
  tripContext: string;
  sourcePOIs: RecommendationContext[];
  alternativeSolutions: Record<string, ISolutionVersion[]>;

  themeTrips: TripWithPois[];
  tripPrice: Price[];
  tripSuggestions: SuggestionGroup[];

  // groupBudget: BudgetFilterValue;
  // destBudget: BudgetFilterValue;
  solutionBudget: BudgetFilterValue;

  solutionTravelers: TravelersFilterValue;

  solutionImg: string;
  destName: string;
  selectedSolutionFilters: FilterTag[];
  availableSolutionFilters: FilterTag[];
  isSolutionSustanable: boolean;
  destOptions: Record<string, Trip[]>;
  nearbyLocations: NearbyTripOptions | null;

  solutionStart: number;
  solutionEnd: number;
  availableTripOptions: Trip[];
  selectedTheme: string;
  recommendationsBlackList: Set<string> | null;
  isRecommendationsLoading: boolean;
  initiallySelectedFilters: FilterTag[];
  bookedActivities: Record<string, boolean>;
  fallbackImgs: FallbackImgs;
  solutionName: string;
  tripCategory: string;
  tripFocus: string;
  tripType: string;
  budgetPreferences: BudgetPreferences;
  [UserActivityCategoryEnum.ACCOMMODATION]: IntersectionalFilter[];
  [UserActivityCategoryEnum.DINING]: IntersectionalFilter[];
  [UserActivityCategoryEnum.EXPERIENCE]: IntersectionalFilter[];
}
export interface RecommendationTimeslot {
  time_slot: Timeslot;
}
export interface RecommendationPoi {
  poi: CandidatePoiItem;
  time_slots: RecommendationTimeslot[];
}
export interface RecommendationsResponse {
  recommendations: RecommendationPoi[];
  need_additional_dining_inputs: boolean;
  need_additional_experience_inputs: boolean;
}
export interface MapTagData {
  name: string;
  id: string;
}

export interface TagScore {
  category: UserActivityCategoryEnum;
  score: number;
}

export interface Recommendation {
  TOTAL: number;
  ACCOMMODATION: number;
  EXPERIENCE: number;
  DINING: number;
}

export interface RecommendationMatch {
  name: string;
  match: number;
  recommendation: Recommendation;
}

export const TripIntentDictionary: Record<TripIntent, string> = {
  [TripIntent.MUST_SEE]: 'DEFINITELY',
  [TripIntent.COULD_SEE]: 'INTERESTED',
  [TripIntent.MAYBE]: 'MAYBE',
  [TripIntent.NONE]: '',
};

export interface MinRecommendation {
  name: string;
  id: string;
}

export interface TripAction {
  tripId: string;
  recommendation: MinRecommendation;
  intent: TripIntent;
}

export interface AlternativesCollection {
  solutionId: string;
  response: IGetAlternativesResponse;
}
export interface AlternativesMutationObj extends AlternativesCollection {
  activityId: string;
}

export interface AlternativeActivityAction {
  activityId: string;
  activity: Activity;
  tripId: string;
}

export interface PriceWithExplanation extends Price {
  text: string;
  value: number;
}

export enum ProblmSubmissionRoute {
  GROUP = 'GROUP',
  DESTINATION = 'DESTINATION',
  SOLUTION = 'SOLUTION',
}

export interface ProblemEvaluation {
  confidence: number;
  need_more_inputs: boolean;
}

type GoalBudget = 'Lowest Prices' | 'Mid Range' | 'Best Experience' | 'Luxury';
type NightlyStayBudget = '< $100' | '$100-200' | '$200-400' | '$400-800' | '> $800';
type ExperienceBudget = '< $100' | '$100-200' | '$200-400' | '$400-800' | '> $800';

export interface BudgetPreferences {
  goalBudget: GoalBudget;
  nightlyStayBudget: NightlyStayBudget;
  experienceBudget: ExperienceBudget;
}
